/**
 * OBSERVE (2026-08-02): the unarmed RMB "look" gains a reading. While the
 * over-shoulder look is held with empty hands, the reticle ray is offered to a
 * list of pluggable PROVIDERS (buildings first — NPCs, flora, wrecks can join
 * later); the nearest hit renders as a quiet Meridian-styled window beside the
 * reticle: what the thing is, and whatever the provider knows about it.
 *
 * Frames: the ray is handed to providers in the FOOT PLANET's local frame
 * (buildings, NPCs and furniture all live there), origin = the camera, so the
 * reticle centre and the ray agree exactly.
 */
import type * as THREE from "three";
import type { Planet } from "./world/planet";

/** What an observed thing tells the player. */
export interface ObserveInfo {
  /** Headline — "SMITHY", "TINMERE RESIDENCE". */
  title: string;
  /** Small dim line under the title (the panel appends the distance). */
  subtitle?: string;
  /** Free-form body lines — residents, staff, condition, … */
  lines: string[];
}

export interface ObserveHit { dist: number; info: ObserveInfo }

export interface ObserveProvider {
  /** Best observable along the ray (planet-local), within maxDist, or null. */
  observe(planet: Planet, origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number): ObserveHit | null;
}

/** How far the unarmed look can read things. */
export const OBSERVE_RANGE_M = 60;

/** Seconds the window lingers after the reticle slides off a target — stops the
 *  panel strobing while the ray brushes along a building edge. */
const LINGER_S = 0.35;

export class ObserveSystem {
  private providers: ObserveProvider[] = [];
  private el: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private subEl: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private lingerT = 0;
  private shownKey = "";

  constructor() {
    this.el = document.createElement("div");
    this.el.className = "observe-panel";
    this.titleEl = document.createElement("div");
    this.titleEl.className = "observe-title";
    this.subEl = document.createElement("div");
    this.subEl.className = "observe-sub";
    this.bodyEl = document.createElement("div");
    this.el.append(this.titleEl, this.subEl, this.bodyEl);
    document.body.appendChild(this.el);
  }

  addProvider(p: ObserveProvider): void {
    this.providers.push(p);
  }

  /** Per-frame. `active` = unarmed look held on foot; ray is planet-local. */
  update(active: boolean, planet: Planet | null,
    origin: THREE.Vector3 | null, dir: THREE.Vector3 | null, dt: number): void {
    if (!active || !planet || !origin || !dir) {
      this.lingerT = 0;
      this.hide();
      return;
    }
    let best: ObserveHit | null = null;
    const perProvider: (number | null)[] = [];
    for (const p of this.providers) {
      const h = p.observe(planet, origin, dir, OBSERVE_RANGE_M);
      perProvider.push(h ? h.dist : null);
      if (h && (best === null || h.dist < best.dist)) best = h;
    }
    // DEV tap: the LIVE reticle ray + per-provider results, for probes and
    // in-game diagnosis (__obsDebug in the console while holding the look).
    if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
      (window as unknown as Record<string, unknown>).__obsDebug = {
        at: performance.now(),
        planet: planet.def.id,
        o: [origin.x, origin.y, origin.z],
        d: [dir.x, dir.y, dir.z],
        perProvider,
        best: best ? { title: best.info.title, dist: best.dist } : null,
      };
    }
    if (best === null) {
      this.lingerT -= dt;
      if (this.lingerT <= 0) this.hide();
      return;
    }
    this.lingerT = LINGER_S;
    this.show(best);
  }

  private show(hit: ObserveHit): void {
    const { info } = hit;
    const distTxt = `${Math.round(hit.dist)} m`;
    const key = `${info.title}|${info.subtitle ?? ""}|${info.lines.join("|")}|${distTxt}`;
    if (key !== this.shownKey) {
      this.shownKey = key;
      this.titleEl.textContent = info.title;
      this.subEl.textContent = info.subtitle ? `${info.subtitle} · ${distTxt}` : distTxt;
      this.bodyEl.replaceChildren(...info.lines.map((line) => {
        const d = document.createElement("div");
        d.className = "observe-line";
        d.textContent = line;
        return d;
      }));
    }
    this.el.classList.add("on");
  }

  private hide(): void {
    this.el.classList.remove("on");
    this.shownKey = "";
  }
}
